import { qoursesApi } from '@/api/qourses.tsx'
import { CourseGroupName } from '@/components/Resolvers.tsx'
import Dynamic from '@/components/modals/dynamic.tsx'
import { popAllModals } from '@/components/modals/index.tsx'
import useGetCourseGroup, {
  getCourseGroupQueryKey,
} from '@/hooks/courseGroups/useGetCourseGroup.tsx'
import { getCourseGroupsQueryKey } from '@/hooks/courses/useGetCourseGroups.tsx'
import { Button } from '@/shadcn/components/ui/button.tsx'
import { DialogDescription, DialogHeader, DialogTitle } from '@/shadcn/components/ui/dialog.tsx'
import { ToastVariant, minDelay, sendGenericErrorNotification, sendNotification } from '@/utils.tsx'
import { useQueryClient } from '@tanstack/react-query'
import { ArchiveRestore, GroupIcon, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export default function RestoreCourseGroupModal({ courseGroupId }: { courseGroupId: string }) {
  const { courseGroup, isLoading, isError } = useGetCourseGroup(courseGroupId)
  const [isRestoring, setIsRestoring] = useState(false)

  const queryClient = useQueryClient()

  const { t: translate } = useTranslation()

  const handleRestoreCourseGroup = async () => {
    try {
      setIsRestoring(true)
      await minDelay(
        qoursesApi.courseGroup.courseGroupControllerRestoreCourseGroupV1(courseGroup.id),
        1000,
      )
      sendNotification(
        translate('modals.restoreCourseGroupModal.notification.title'),
        translate('modals.restoreCourseGroupModal.notification.subtitle'),
        ToastVariant.Success,
      )
      popAllModals()
      queryClient.invalidateQueries(getCourseGroupsQueryKey({}))
      queryClient.invalidateQueries(getCourseGroupQueryKey(courseGroupId))
    } catch (e) {
      console.error(e)
      sendGenericErrorNotification()
    } finally {
      setIsRestoring(false)
    }
  }

  if (isLoading) {
    return (
      <Dynamic.Content>
        <DialogHeader>
          <DialogTitle>{translate('common.loading')}</DialogTitle>
        </DialogHeader>
      </Dynamic.Content>
    )
  }

  if (isError) {
    return (
      <Dynamic.Content>
        <DialogHeader>
          <DialogTitle>{translate('modals.restoreCourseGroupModal.errorState.title')}</DialogTitle>
          <DialogDescription>
            {translate('modals.restoreCourseGroupModal.errorState.subtitle')}
          </DialogDescription>
        </DialogHeader>
      </Dynamic.Content>
    )
  }

  return (
    <Dynamic.Content className="p-4 sm:p-8">
      <DialogHeader className="mb-2">
        <DialogHeader className="mb-2">
          <DialogTitle className="mt-6 sm:mt-2"></DialogTitle>
          <DialogTitle className="mt-6 flex flex-col gap-y-2 sm:mt-2">
            <div className="flex flex-row items-center justify-center text-sm text-muted-foreground sm:justify-start">
              <GroupIcon className="mr-1 size-4 text-muted-foreground" />
              <CourseGroupName courseGroupId={courseGroupId} />
            </div>
            <p>{translate('modals.restoreCourseGroupModal.title')}</p>
          </DialogTitle>
          <DialogDescription>
            {translate('modals.restoreCourseGroupModal.subtitle')}
          </DialogDescription>
        </DialogHeader>
      </DialogHeader>
      <Button
        variant="indigo"
        className="bezel"
        disabled={isRestoring}
        onClick={handleRestoreCourseGroup}
      >
        {isRestoring ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {translate('common.loading')}
          </>
        ) : (
          <>
            <ArchiveRestore className="mr-2 h-4 w-4" />
            {translate('modals.restoreCourseGroupModal.button')}
          </>
        )}
      </Button>
    </Dynamic.Content>
  )
}
